import { Button, type ComboboxItem } from "@mantine/core";
import { useState } from "react";
import ReactDOM from "react-dom";
import { type FieldError, type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useToast } from "../../../../mantine/notifications/useToast";
import type {
  AnlageStromstgListData,
  Generator,
  Site
} from "../../../../utils/backend-types";
import { trimString } from "../../../../utils/trimString";
import { FormFieldController } from "../../../BuildingBlocks/Forms/Controllers/FormFieldController";
import { setErrorsFromResponseData } from "../../../BuildingBlocks/Forms/utils/setErrorsFromResponseData";
import { getGeneratorNameWithSite } from "../../utils/getGeneratorNameWithSite";
import type { AnlageStromstgSelectionData } from "./AnlageStromstgForm.constants";
import { ANLAGE_STROMSTG_FORM_FIELDS } from "./AnlageStromstgForm.constants";

function getInitiallySelectedSiteIds(
  initiallySelectedGenerators: Array<AnlageStromstgListData>,
  sites: Array<Site>
) {
  const allSiteIds = initiallySelectedGenerators
    .map((generator) =>
      sites.find((site) => site.name === generator.site)?.id?.toString()
    )
    .filter((siteId) => typeof siteId !== "undefined");

  return Array.from(new Set(allSiteIds));
}

interface AnlageStromstgFormProps {
  buttonContainer?: HTMLElement;
  cancelButtonText: "Abbrechen" | "Überspringen";
  currentGeneratorId: number;
  generators: Array<Generator>;
  initiallySelectedGenerators: Array<AnlageStromstgListData>;
  multiSite?: boolean;
  sites: Array<Site>;
  onSkip: () => void;
  onSubmit: (selectedValues: Array<number>) => Promise<void>;
}

function AnlageStromstgForm({
  buttonContainer,
  cancelButtonText,
  generators,
  currentGeneratorId,
  initiallySelectedGenerators,
  multiSite,
  sites,
  onSkip,
  onSubmit
}: AnlageStromstgFormProps) {
  const { showErrorToast } = useToast();
  const { t } = useTranslation();
  const currentSiteId = generators.find(
    (generator) => generator.id === currentGeneratorId
  )?.site;
  const {
    control,
    formState: { errors },
    handleSubmit,
    setError,
    watch
  } = useForm<AnlageStromstgSelectionData>({
    defaultValues: {
      generatorIds: initiallySelectedGenerators
        .map((generator) => generator.id.toString())
        .filter((id) => id !== currentGeneratorId.toString()),
      site: getInitiallySelectedSiteIds(
        initiallySelectedGenerators,
        sites
      ).filter((id) => id !== currentSiteId?.toString())
    }
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onValidSubmit: SubmitHandler<AnlageStromstgSelectionData> = async (
    data
  ) => {
    setIsSubmitting(true);

    const generatorIdsToSubmit = [
      currentGeneratorId,
      ...data.generatorIds.map((id) => Number(id))
    ];

    try {
      const response = await onSubmit(generatorIdsToSubmit);
      return response;
    } catch (error) {
      setErrorsFromResponseData<AnlageStromstgSelectionData>(
        showErrorToast,
        error,
        watch(),
        setError,
        t("errors.UnknownError")
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitCallback = handleSubmit(onValidSubmit);

  const generatorsFilteredBySite = generators.filter((generator) => {
    const sites = watch("site");

    if (!sites || sites.length === 0) {
      return true;
    }

    return (
      sites.includes(generator.site.toString()) ||
      generator.site === currentSiteId
    );
  });

  const generatorNamesWithIds = generatorsFilteredBySite.map((generator) => {
    const trimmedGeneratorName = trimString(generator.name);
    const generatorNameWithSite = getGeneratorNameWithSite(
      trimmedGeneratorName,
      generator.site,
      sites
    );

    return {
      id: generator.id,
      name: generatorNameWithSite
    };
  });

  const generatorIdChoices = generatorNamesWithIds
    ?.map<ComboboxItem>((generator) => {
      return {
        value: generator.id.toString(),
        label: generator.name
      };
    })
    .filter((choice) => choice.value !== currentGeneratorId.toString());

  const siteChoices = sites
    .map<ComboboxItem>((site) => {
      return {
        value: site.id.toString(),
        label: site.name
      };
    })
    .filter((choice) => choice.value !== currentSiteId?.toString());

  return (
    <form className="AnlageStromstgForm" onSubmit={submitCallback}>
      {multiSite && (
        <FormFieldController
          control={control}
          data={{
            ...ANLAGE_STROMSTG_FORM_FIELDS.site,
            data: siteChoices
          }}
          error={errors.site as FieldError | undefined}
        />
      )}
      <FormFieldController
        control={control}
        data={{
          ...ANLAGE_STROMSTG_FORM_FIELDS.generatorIds,
          data: generatorIdChoices ?? []
        }}
        error={errors.generatorIds as FieldError | undefined}
      />
      {buttonContainer &&
        ReactDOM.createPortal(
          <>
            <Button disabled={isSubmitting} variant="default" onClick={onSkip}>
              {cancelButtonText}
            </Button>
            <Button
              color="secondary"
              disabled={isSubmitting}
              ml="xs"
              type="submit"
              onClick={submitCallback}
            >
              Speichern
            </Button>
          </>,
          buttonContainer
        )}
    </form>
  );
}

export { AnlageStromstgForm, AnlageStromstgFormProps };
